import { rateText, scoreLabels } from 'components/ReceptionScore/model';
import styled from '@emotion/styled';
import { Button, TableColumnsType, Tag, Tooltip } from 'antd';
import ScrollTable, { ScrollTableProps } from 'components/ScrollTable';
import { formatNumber } from 'utils/numberFormat';
import { ReactNode } from 'react';
import { What } from 'views/Marketing/components/PolicyTable';
import { useBuyerTypeLabelMap } from './AssistantDetailtable';

type RT = Dashboard.ReceptionScore.Stat;

export interface StatTableProps extends ScrollTableProps<RT> {
  onCheckDetail: (record: RT) => void;
}

function StatTable(props: StatTableProps) {
  const { onCheckDetail, ...otherProps } = props;

  const { t, buyerTypeLabelMap } = useBuyerTypeLabelMap();

  const dataRender = (total: ReactNode, preSale: ReactNode, afterSale: ReactNode) => {
    return (
      <Data>
        {total}
        <div>
          <Tag color="blue">
            {buyerTypeLabelMap.PRE_SALE}
            {preSale}
          </Tag>
        </div>
        <div>
          <Tag color="orange">
            {buyerTypeLabelMap.AFTER_SALE}
            {afterSale}
          </Tag>
        </div>
      </Data>
    );
  };

  const reasonColumns: TableColumnsType<RT> = scoreLabels
    .filter((label) => label.code !== 'POSITIVE')
    .map((label) => ({
      key: label.code,
      width: 180,
      title: t(`receptionScore.label.${label.code}`, label.name),
      render: (_, row) => {
        const code = label.code.replace('NEGATIVE_', '');
        return `${row.reasonCounts?.[code] ?? 0} / ${rateText(row.reasonRatios?.[code])}`;
      },
    }));
  const columns: TableColumnsType<RT> = [
    {
      key: 'assistantId',
      width: 100,
      dataIndex: 'assistantId',
      title: 'ID',
      fixed: 'left',
    },
    {
      key: 'assistantName',
      width: 160,
      dataIndex: 'assistantName',
      title: t('aide.帐号昵称'),
      fixed: 'left',
    },
    {
      key: 'inviteCount',
      width: 180,
      dataIndex: 'inviteCount',
      sorter: true,
      showSorterTooltip: false,
      title: (
        <div>
          {t('aide.评分邀请量')}
          <Tooltip title={t('aide.系统发起评分邀请的总数量')}>
            <What />
          </Tooltip>
        </div>
      ),
      render: (_, record) => {
        const { inviteCount, preSaleInviteCount, afterSaleInviteCount } = record;

        const formatter = (num: number) => formatNumber(num, '0,0');

        return dataRender(
          formatter(inviteCount),
          formatter(preSaleInviteCount),
          formatter(afterSaleInviteCount),
        );
      },
    },
    {
      key: 'replyRate',
      width: 180,
      title: (
        <div>
          {t('aide.评分率')}
          <Tooltip
            title={t('aide.系统发起评分邀请后，买家给出评分的比例 (买家评分量 / 评分邀请量)')}
          >
            <What />
          </Tooltip>
        </div>
      ),
      sorter: (a, b) => parseFloat(a.replyRate) - parseFloat(b.replyRate),
      showSorterTooltip: false,
      render: (_, record) => {
        const { replyRate, preSaleReplyRate, afterSaleReplyRate } = record;

        return dataRender(replyRate, preSaleReplyRate, afterSaleReplyRate);
      },
    },
    {
      key: 'praiseRate',
      width: 180,
      title: (
        <div>
          {t('aide.好评率')}
          <Tooltip title={t('aide.系统发起评分邀请后，买家给出好评的比例 (好评量/买家评分量)')}>
            <What />
          </Tooltip>
        </div>
      ),
      sorter: (a, b) => parseFloat(a.praiseRate) - parseFloat(b.praiseRate),
      showSorterTooltip: false,
      render: (_, record) => {
        const { preSalePraiseRate, afterSalePraiseRate } = record;

        return dataRender(
          rateText(record.positiveRate),
          preSalePraiseRate,
          afterSalePraiseRate,
        );
      },
    },
    {
      key: 'negativeCount',
      width: 120,
      dataIndex: 'negativeCount',
      title: t('receptionScore.negativeCount', '差评数量'),
    },
    {
      key: 'negativeRate',
      width: 120,
      dataIndex: 'negativeRate',
      title: t('receptionScore.negativeRate', '差评率'),
      render: rateText,
    },
    ...reasonColumns,
    {
      key: 'operation',
      width: 160,
      title: t('操作'),
      fixed: 'right',
      render: (_, record) => {
        return (
          <Button type="link" onClick={() => onCheckDetail(record)}>
            {t('aide.查看评分明细')}
          </Button>
        );
      },
    },
  ];

  const tableWidth = columns.reduce((total, column) => total + Number(column.width), 0);

  return (
    <>
      <p>
        {t(
          'receptionScore.ratioHelp',
          '好评率、差评率以全部评价为分母；各原因列为“差评数量 / 占全部差评比例”。时间按邀请发送时间统计。',
        )}
      </p>
      <ScrollTable
        rowKey="assistantId"
        columns={columns}
        scroll={{ x: tableWidth }}
        {...otherProps}
      />
    </>
  );
}

const Data = styled.div`
  > div {
    margin-top: 4px;
  }
`;

export default StatTable;
